import React, { useState, useMemo, useEffect, useRef } from 'react'
import { IconButton, Typography, Box } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import dayjs from 'dayjs'
import { useRouteQuery, useTranslator } from '@/hooks'

interface FilterMonthAttendanceProps {
  selectedDate?: Date
  startDate?: Date
  endDate?: Date
  onDateChange?: (dateString: string) => void
  className?: string
  isShowHistory?: boolean
  onShowHistory?: () => void
  isCurrentPayPeriod?: string
  dataBeforeMonth?: any
  showToday?: boolean
}

const FilterMonthAttendance: React.FC<FilterMonthAttendanceProps> = ({
  selectedDate,
  startDate,
  endDate,
  onDateChange,
  className = '',
  isShowHistory = false,
  onShowHistory,
  isCurrentPayPeriod,
  dataBeforeMonth,
  showToday = true
}) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const monthFromUrl = searchQuery.get('month')

  const getInitialDate = () => {
    if (monthFromUrl) {
      return dayjs(monthFromUrl).toDate()
    }

    if (selectedDate) {
      return selectedDate
    }

    return new Date()
  }

  const [currentDate, setCurrentDate] = useState<Date>(getInitialDate())
  const updateQueryRef = useRef(updateQuery)

  updateQueryRef.current = updateQuery

  useEffect(() => {
    if (monthFromUrl) {
      const newDate = dayjs(monthFromUrl).toDate()

      setCurrentDate(newDate)
    }
  }, [monthFromUrl])

  const currentMonth = useMemo(() => {
    return dayjs(currentDate).format('MMMM YYYY')
  }, [currentDate])

  const dateRange = useMemo(() => {
    // Nếu có startDate và endDate từ props, sử dụng chúng
    if (startDate && endDate) {
      return {
        start: dayjs(startDate).format('DD/MM/YYYY'),
        end: dayjs(endDate).format('DD/MM/YYYY')
      }
    }

    // Nếu không có, sử dụng logic cũ với currentDate
    const start = dayjs(currentDate).startOf('month')
    const end = dayjs(currentDate).endOf('month')

    return {
      start: start.format('DD/MM/YYYY'),
      end: end.format('DD/MM/YYYY')
    }
  }, [currentDate, startDate, endDate])

  useEffect(() => {
    if (!searchQuery.get('month')) {
      const today = dayjs().startOf('month').format('YYYY-MM-DD')

      updateQueryRef.current({ month: today })
    }
  }, [searchQuery.get('month')])

  const isCurrentMonth = useMemo(() => {
    return dayjs(currentDate).isSame(dayjs(), 'month')
  }, [currentDate])

  const isNextMonthDisabled = useMemo(() => {
    // Disable khi tháng hiện tại đã lớn hơn hoặc bằng isCurrentPayPeriod
    if (isCurrentPayPeriod) {
      const payPeriodDate = dayjs(isCurrentPayPeriod)
      
      return dayjs(currentDate).isAfter(payPeriodDate, 'month') || dayjs(currentDate).isSame(payPeriodDate, 'month')
    }
    
    // Fallback: disable khi tháng hiện tại đã lớn hơn hoặc bằng tháng hiện tại của hệ thống
    const today = dayjs()
    
    return dayjs(currentDate).isAfter(today, 'month') || dayjs(currentDate).isSame(today, 'month')
  }, [currentDate, isCurrentPayPeriod])

  const isPreviousMonthDisabled = useMemo(() => {
    // Disable khi không có dataBeforeMonth
    if (dataBeforeMonth === null || dataBeforeMonth === undefined) {
      return true
    }

    // Nếu là array, check length
    if (Array.isArray(dataBeforeMonth)) {
      return dataBeforeMonth.length === 0
    }

    // Nếu là object, check có data không
    if (typeof dataBeforeMonth === 'object') {
      return Object.keys(dataBeforeMonth).length === 0
    }

    return false
  }, [dataBeforeMonth])

  const handlePreviousMonth = () => {
    const newDate = dayjs(currentDate).subtract(1, 'month')

    setCurrentDate(newDate.toDate())

    const formattedDate = newDate.startOf('month').format('YYYY-MM-DD')

    onDateChange?.(formattedDate)

    updateQuery({ month: formattedDate })
  }

  const handleNextMonth = () => {
    const newDate = dayjs(currentDate).add(1, 'month')

    setCurrentDate(newDate.toDate())

    const formattedDate = newDate.startOf('month').format('YYYY-MM-DD')

    onDateChange?.(formattedDate)

    updateQuery({ month: formattedDate })
  }

  const handleToday = () => {
    const today = dayjs()

    setCurrentDate(today.toDate())

    const formattedDate = today.startOf('month').format('YYYY-MM-DD')

    onDateChange?.(formattedDate)

    updateQuery({ month: formattedDate })
  }

  return (
    <Box className={`flex items-center justify-between bg-white ${className}`}>
      <div className='flex items-center gap-3'>
        <Box className='flex items-center gap-3'>
          <IconButton 
            onClick={handlePreviousMonth} 
            size='small' 
            className='text-gray-600 hover:text-gray-800'
            disabled={isPreviousMonthDisabled}
          >
            <i className='tabler-chevron-left' />
          </IconButton>

          <IconButton
            onClick={handleNextMonth}
            size='small'
            className='text-gray-600 hover:text-gray-800'
            disabled={isNextMonthDisabled}
          >
            <i className='tabler-chevron-right' />
          </IconButton>

          <Typography variant='h6' className='text-gray-800 font-medium text-[24px]'>
            {currentMonth}
          </Typography>
        </Box>
      </div>

      <Box className='flex items-center gap-3'>
        <Button variant='outlined' size='small' className='border-blue-500 text-blue-600 hover:border-blue-600'>
          <span className='text-[15px] font-medium'>
            {dateRange.start} - {dateRange.end}
          </span>
        </Button>

        {showToday && (
          <Button variant='tonal' size='small' color='primary' onClick={handleToday} disabled={isCurrentMonth}>
            {__('Current Month')}
          </Button>
        )}

        {isShowHistory && (
          <Button
            variant='tonal'
            size='small'
            color='primary'
            startIcon={<i className='tabler-history' />}
            className='bg-[#00bad129] text-[#00bad1]'
            onClick={onShowHistory}
          >
            {__('History')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default FilterMonthAttendance
